import "../../assets/ContentEditor.css";
import { useState, useEffect } from "react";
import { Dialog } from '../dialogs/dialog';
import { ExplainedButton as Button } from '../explained-button';
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { ManagedFragmentDto, FragmentActionDto, ActionType, ValidateFormPayloadDto } from "../../general/dtos";
import { useManagedModuleEditor } from "../../providers/managed-module-management-provider";

interface ActionModalProps {
  mode: "add" | "edit";
  fragment: ManagedFragmentDto;
  action?: FragmentActionDto;
  onSave: (action: FragmentActionDto) => void;
  onCancel: () => void;
}

export function ActionModal({
  mode,
  fragment,
  action,
  onSave,
  onCancel
}: ActionModalProps) {
  const { fragments } = useManagedModuleEditor();

  const [label, setLabel] = useState(action?.label ?? "Next");
  const [type, setType] = useState<ActionType>(action?.type ?? "gotoNext");
  const [to, setTo] = useState<string>(action?.to ?? "next");
  const [toOnAllCorrect, setToOnAllCorrect] = useState<string>("self");
  const [toOnIncorrect, setToOnIncorrect] = useState<string>("self");

  useEffect(() => {
    if (type === "gotoNext") setTo("next");
    else if (type === "gotoPrevious") setTo("prev");
    else if (type === "gotoFragment") {
      // default to first fragment if none selected
      const first = fragments[0]?.key ?? "";
      setTo(action?.to ?? first);
    } else if (type === "validateForm") {
      setTo(action?.to ?? "self");
      const payload: ValidateFormPayloadDto | null = action?.payload as ValidateFormPayloadDto;
      if (payload) {
        setToOnAllCorrect(payload.toOnAllCorrect ?? "self");
        setToOnIncorrect(payload.toOnIncorrect ?? "self");
      }
    } else if (type === 'compareAnswers') setTo('self');
  }, [type, action?.to, action?.payload, fragments]);

  const getNextAvailableOrder = (actions: FragmentActionDto[]): number => {
    const existingOrders = new Set(actions.map(a => a.order));
    let candidate = 0;

    // Increment candidate until we find a number not in the set
    while (existingOrders.has(candidate)) {
        candidate++;
    }

    return candidate;
  };

  const handleSave = () => {
    let payload: any = action?.payload ?? undefined;
    if (type === "validateForm") {
        payload = { toOnAllCorrect: toOnAllCorrect, toOnIncorrect: toOnIncorrect};
    }
    if (type === 'compareAnswers') payload = undefined;
    const newAction: FragmentActionDto = {
      key: action?.key ?? crypto.randomUUID(),
      order: action?.order ?? getNextAvailableOrder(fragment.actions),
      label,
      type,
      to,
      payload: payload
    };

    onSave(newAction);
  };

  const typeOptions = [
    { label: "Go to next fragment", value: "gotoNext", defaultLabel: "Next", canDuplicate: false },
    { label: "Go to previous fragment", value: "gotoPrevious", defaultLabel: "Back", canDuplicate: false },
    { label: "Go to specific fragment", value: "gotoFragment", defaultLabel: "", canDuplicate: true },
    { label: "Verify user input", value: "validateForm", defaultLabel: "Your Score" },
    { label: "Show answers", value: "showForm", defaultLabel: "Show answers", canDuplicate: false },
    { label: "Reset user input", value: "resetForm", defaultLabel: "Start Again", canDuplicate: false },
    { label: "Compare answers", value: "compareAnswers", defaultLabel: "Compare answers", canDuplicate: false }
  ];

  const getOptimizedOptions = () => {
    if (mode === "add" && fragment) {
      const notIncluded = typeOptions.filter(o => o.canDuplicate || !fragment.actions.find(a => a.type === o.value));
      return notIncluded;
    }
    return typeOptions;
  };

  const fragmentOptions = fragments.map(f => ({
    label: f.title,
    value: f.key
  }));

  const targetsWithSelf = [
    { label: "Stay on this fragment", value: "self" },
    ...fragmentOptions
  ];

  const comparisonAlreadyPresent = type === 'compareAnswers' && fragment.actions.some(item => item.type === type && item.key !== action?.key);
  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button label="Cancel" className="p-button-text" onClick={onCancel} />
      <Button label="Save" onClick={handleSave} disabled={!label.trim() || comparisonAlreadyPresent}
        disabledReason={comparisonAlreadyPresent ? 'This fragment already has a Compare answers action.' : 'Enter a label for this action first.'} />
    </div>
  );

  return (
    <Dialog
      header={mode === "add" ? "Add action" : "Edit action"}
      visible
      modal
      style={{ width: "450px" }}
      onHide={onCancel}
      footer={footer}
    >
      <div className="flex flex-column gap-3">

        {/* Label */}
        <div className="flex flex-column gap-1">
          <label className="font-semibold">Label</label>
          <InputText
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Button label"
          />
        </div>

        {/* Type */}
        <div className="flex flex-column gap-1">
          <label className="font-semibold">Action type</label>
          <Dropdown
            value={type}
            scrollHeight="300px"
            options={getOptimizedOptions()}
            onChange={(e) => {
                const typeOption = typeOptions.find(o => o.value === type); // label contains previous value, so we see if the default was selected previously
                const newTypeOption = typeOptions.find(o => o.value === e.value);
                setType(e.value);
                if (label.length === 0 || label === typeOption?.defaultLabel) {
                    setLabel(newTypeOption?.defaultLabel ?? "");
                }
            }}
            placeholder="Select action type"
          />
        </div>

        {/* Destination */}
        {type === 'compareAnswers' && <p>This button compares answers using the course mappings where this is the later fragment.
          It appears only for learners assigned to an applicable course. No course or comparison ID is stored in this shared module action.
          Author previews show the button disabled because they have no learner course context.</p>}
        {type === "gotoFragment" && (
          <div className="flex flex-column gap-1">
            <label className="font-semibold">Target fragment</label>
            <Dropdown
              value={to}
              options={fragmentOptions}
              onChange={(e) => setTo(e.value)}
              placeholder="Select fragment"
              scrollHeight={'500px'}
            />
          </div>
        )}

        {type === "validateForm" && (<>
          <div className="flex flex-column gap-1">
            <label className="font-semibold">Go to target (default)</label>
            <Dropdown
              value={to}
              options={targetsWithSelf}
              onChange={(e) => setTo(e.value)}
              placeholder="Select target"
              scrollHeight={'500px'}
            />
          </div>
          <div className="flex flex-column gap-1">
            <label className="font-semibold">Go to target (if all answers correct)</label>
            <Dropdown
              value={toOnAllCorrect}
              options={targetsWithSelf}
              onChange={(e) => setToOnAllCorrect(e.value)}
              placeholder="Select target"
              scrollHeight={'500px'}
            />
          </div>
          <div className="flex flex-column gap-1">
            <label className="font-semibold">Go to target (if any incorrect)</label>
            <Dropdown
              value={toOnIncorrect}
              options={targetsWithSelf}
              onChange={(e) => setToOnIncorrect(e.value)}
              placeholder="Select target"
              scrollHeight={'500px'}
            />
          </div>
        </>)}
      </div>
    </Dialog>
  );
}
